import { WebSocketWrapper } from 'data/types'
import { useEffect, useState } from 'react'
import webSocket from 'services/api/webSocket'

const MAX_RETRIES = 3
const DELAY_BEFORE_RETRY = 5000
const MAX_MISSED_HEARTBEATS = 5
const HEARTBEAT_TIME = 5000

const createWS = (url: string): WebSocket => {
  if (window.mocks?.enabled) {
    return window.mocks.getWS()
  }

  return new WebSocket(url)
}

type Options = {
  onMessage: (message: any) => void
  onOpen?: (s: WebSocketWrapper) => void
}

export default function useWS(url: string, options?: Options) {
  // attempts to restablish connection
  const [attempts, setAttempts] = useState(0)
  // reconnection attemps count. Used to force an effect and reconnect
  // attemps counts the iterations to establish a connection
  // while reconnectIteration counts the times the connection dropped and we
  // try again to connect
  const [reconnectIteration, setReconnectIteration] = useState(0)
  const [exportedWs, setExportedWS] = useState<WebSocket | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState(false)
  const [lastHeartbeat, setLastHeartbeat] = useState(0)

  useEffect(
    function setupWSconnectionHook() {
      performance.mark('WS:start')

      exportedWs?.close()
      setExportedWS(null)
      setConnecting(true)

      if (attempts >= MAX_RETRIES) {
        // We failed enough times, just call out and error and leave
        setError(true)
        setConnecting(false)
        return
      }

      const ws = createWS(url)

      // We export a wrapper with limited interface so we control what goes
      // though the wire
      const wrapper: WebSocketWrapper = {
        send: (msg: object) => {
          if (ws.readyState !== 1) return
          ws.send(JSON.stringify(msg))
        }
      }

      ws.onmessage = (message) => {
        if (typeof message.data !== 'string') {
          // eslint-disable-next-line no-console
          console.error('Received not string WS message', message)
          return
        }

        const data = JSON.parse(message.data)
        if (!data) {
          // eslint-disable-next-line no-console
          console.error('WS message string cannot be parsed', message)
          return
        }

        options?.onMessage(data)

        if (data.channel === webSocket.channel.HEARTBEAT) {
          setLastHeartbeat(Date.now())
        }
      }

      ws.onopen = () => {
        performance.mark('WS:onOpen')
        if (ws.readyState === 1) {
          performance.mark('WS:done')
          performance.measure('WS', 'WS:start', 'WS:done')
          performance.measure('All set', 'DOM:start', 'WS:done')

          const channel = webSocket.channel.HEARTBEAT
          wrapper.send({ action: webSocket.action.SUBSCRIBE, channel })

          if (options?.onOpen) options?.onOpen(wrapper)
          setConnecting(false)
          setExportedWS(ws)
        }
      }

      ws.onerror = () => {
        performance.mark('WS:onError')
        setTimeout(() => setAttempts(attempts + 1), DELAY_BEFORE_RETRY)
      }

      ws.onclose = () => performance.mark('WS:onClose')

      return () => ws.close()
    },
    [attempts, reconnectIteration]
  )

  useEffect(
    function checkHeartStillBeatsHook() {
      // not ready yet
      if (lastHeartbeat === 0) return

      // check heatbeats!
      const timeout = setInterval(() => {
        const lastHeartbeatIsOld =
          lastHeartbeat + HEARTBEAT_TIME * MAX_MISSED_HEARTBEATS < Date.now()
        if (!lastHeartbeatIsOld) return // all good, keep going

        // force reconnection
        setAttempts(0)
        setLastHeartbeat(0)
        setReconnectIteration(reconnectIteration + 1)
      }, HEARTBEAT_TIME)

      // clear old check, we are setting a new one
      return () => clearInterval(timeout)
    },
    [lastHeartbeat]
  )

  return {
    connecting, // true while establishing a connection or reconnecting, false otherwise
    error, // true if there was an error establishing aconnection
    ready: exportedWs !== null, // true only when we got a connection ready to send and receive
    ws: exportedWs // the WS object to send and receive messages, or null if not ready
  }
}
